import React, { Component, createContext, useContext, useEffect, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';
import moment from 'moment';
import { truncate } from 'lodash';
import { TableToolbarSelect, debounceSearchRender, TableFilterList } from 'mui-datatables';
import {
  Box,
  Button,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Alert,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import { SyncAlt, Mail as MailIcon, Delete as DeleteIcon, Restore as RestoreIcon } from '@mui/icons-material';
import superSearch from '@codewell/super-search';
import { isUndefinedOrNull } from '../helpers';
import * as usersModel from '../models/users';
import routes from '../routes';
import * as authService from '../services/auth';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import OverlayLoader from './OverlayLoader';
import ConfirmationDialog from './ConfirmationDialog';
import TableActionButton from './TableActionButton';
import TableActionsButtonsWrapper from './TableActionsButtonsWrapper';
import { getUserFullName } from '../helpers/user';
import HtmlTooltip from './Common/HtmlTooltip';
import { getMuiTableDataIndex, getRoleAssignment, getRoleAssignmentEntityType } from '../helpers/getters';
import { formatDate, formatUserRoles, formatUserRole, generateCustomCsvFileName } from '../helpers/formatters';
import { Roles } from '../models/roles';
import { sortAlphabetically, sortSelectedFirst } from '../helpers/sorters';
import { rolesWeights } from '../models/users';
import {
  RegistrationProcessFilter,
  userPhoneVerifiedFilterOptions,
  userRegistrationFilterOptions,
} from '../helpers/MuiTableCustomFilters';
import { FOOD_DONOR_TYPE, RECEIVER_TYPE } from '../containers/UsersListView';
import CopyInviteLinkSplitButton from './CopyInviteLinkSplitButton';

const FiltersContext = createContext({
  groupsList: [],
  selectedGroup: null,
  onGroupChange: () => {},
  affiliationsList: [],
  affiliationLoading: false,
  handleAffiliationSelect: () => {},
});

const CustomFilterList = ({ filterList, filterUpdate, ...restProps }) => {
  const { columnNames } = restProps;
  const { groupsList, selectedGroup, onGroupChange, affiliationsList, affiliationLoading, handleAffiliationSelect } =
    useContext(FiltersContext);
  const [affiliationInputValue, setAffiliationInputValue] = useState('');
  const [affiliationOptions, setAffiliationOptions] = useState([]);

  const loadSuggestionsMinChars = 2;
  const groupColIndex = columnNames.findIndex(col => col.name === 'group_filter');

  useEffect(() => {
    if (affiliationInputValue.length <= loadSuggestionsMinChars) {
      return setAffiliationOptions([]);
    }

    return setAffiliationOptions(affiliationsList);
  }, [affiliationInputValue]);

  const wrappedFilterUpdate = (index, value, ...args) => {
    if (index === groupColIndex) {
      onGroupChange(null);
      return;
    }
    filterUpdate(index, value, ...args);
  };

  const registrationCompleteStatusColumnIndex = columnNames.findIndex(col => col.name === 'completed_registration');
  const registrationCompleteStatusColumn = columnNames[registrationCompleteStatusColumnIndex];

  const updatedList = [...filterList];
  const [completeRegistrationFilterType, completeRegistrationFilterValue] = filterList[
    registrationCompleteStatusColumnIndex
  ];

  const completeRegistrationFilterTypeCustom = completeRegistrationFilterType === 'custom';

  const completeRegistrationChecked =
    completeRegistrationFilterTypeCustom ||
    [
      RegistrationProcessFilter.completed,
      RegistrationProcessFilter.custom,
      RegistrationProcessFilter.last30Days,
      RegistrationProcessFilter.last90Days,
    ].includes(completeRegistrationFilterValue);

  if (completeRegistrationFilterValue === RegistrationProcessFilter.completed) {
    updatedList[registrationCompleteStatusColumnIndex] = [];
  }

  if (groupColIndex >= 0) {
    updatedList[groupColIndex] = selectedGroup ? [selectedGroup.name] : [];
  }

  return (
    <>
      <Box pl={2} pb={1}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>

          <Grid item>
            <Autocomplete
              loading={affiliationLoading}
              size="small"
              placeholder="Affiliation"
              multiple
              sx={{ minWidth: 220, maxWidth: 450 }}
              options={affiliationOptions}
              onInputChange={(event, value) => {
                setAffiliationInputValue(value);
              }}
              onChange={(event, value) => handleAffiliationSelect(value)}
              getOptionLabel={(option) => option.name}
              noOptionsText={affiliationInputValue.length <= loadSuggestionsMinChars ? 'Type the 3 first letters to see results' : 'User not found'}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...props } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.name}
                      size="small"
                      {...props}
                      sx={{
                        backgroundColor: option.type === FOOD_DONOR_TYPE ? 'primary.light' : 'appbar.light',
                        color: 'primary.contrastText',
                        '& .MuiChip-deleteIcon': {
                          color: option.type === FOOD_DONOR_TYPE ? 'primary.contrastText' : 'appbar.contrastText',
                          '&:hover': {
                            color: option.type === FOOD_DONOR_TYPE ? 'primary.dark' : 'appbar.dark',
                          },
                        },
                      }}
                    />
                  );
                })
              }
              renderOption={(props, option) => {
                const {key, ...rest} = props;
                return (
                  <li {...rest} key={`${key}_${option.type}`}>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography color="textSecondary" variant="body2">
                        {option.type}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Affiliation" variant="outlined" />}
            />
          </Grid>

          {groupsList && groupsList.length > 0 && (
            <Grid item>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="group-filter-label" shrink>Filter by Group</InputLabel>
                <Select
                  labelId="group-filter-label"
                  value={selectedGroup ? selectedGroup.id : ''}
                  label="Filter by Group"
                  displayEmpty
                  renderValue={value => {
                    if (!value) return 'All Users';
                    const group = groupsList.find(g => g.id === value);
                    return group ? group.name : 'All Users';
                  }}
                  onChange={e => {
                    const group = groupsList.find(g => g.id === e.target.value) || null;
                    onGroupChange(group);
                  }}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {groupsList.map(g => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={completeRegistrationChecked}
                  color="secondary"
                  onChange={() => {
                    filterUpdate(
                      registrationCompleteStatusColumnIndex,
                      completeRegistrationChecked ? [] : ['static', 'Completed Registration'],
                      registrationCompleteStatusColumn.name,
                      registrationCompleteStatusColumn.filterType
                    );
                  }}
                />
              }
              label="Registration Complete"
            />
          </Grid>
        </Grid>
      </Box>
      <Box display="flex" flexDirection="row">
        <TableFilterList {...restProps} filterList={updatedList} filterUpdate={wrappedFilterUpdate} />
      </Box>
    </>
  );
};

class UsersTable extends Component {
  state = {
    isDeleting: false,
    selectedRows: [],
    selectedRowsDataIndexes: [],
    selectedGroup: null,
    usersFromDifferentSite: [],
    usersWithoutPrimaryContact: [],
    selectedAffiliations: [],
  };

  componentDidMount() {
    const { users = [], locationState = {} } = this.props;
    const selectedUsers = locationState?.selectedUsers || [];
    const selectedIds = new Set(selectedUsers.map((u) => u?.id));
    const tableDataIds = new Set(users.map((u) => u.id));

    const usersWithoutPrimaryContact = locationState?.usersWithoutPrimaryContact || [];
    const usersFromDifferentSite = (selectedUsers || [])
      .reduce((acc, user) => {
        if (!tableDataIds.has(user?.id)) {
          acc.push(user);
        }

        return acc;
      }, [])
      .filter((user) => user);

    const selectedSiteUsersIds = (users || []).reduce((acc, row) => {
      if (selectedIds.has(row?.id)) {
        acc.push(row.id);
      }

      return acc;
    }, []);

    const selectedSiteUsersIndexes = (users || []).reduce((acc, row, index) => {
      if (selectedIds.has(row?.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    this.setState({
      selectedRowsDataIndexes: selectedSiteUsersIndexes,
      selectedRows: selectedSiteUsersIds,
      usersFromDifferentSite,
      usersWithoutPrimaryContact,
    });
  }

  renderCustomToolbarSelector = selectedRows => {
    const {
      users,
      showSendEmailButton = false,
      showSendSmsButton = false,
      showSendPushButton = false,
      onSendEmailButtonClick,
      onSendSMSButtonClick,
      onSendPushButtonClick,
    } = this.props;
    const selectedRescuers = [];

    selectedRows.data.map(selectedRow => {
      const foundRescuer = users.find(r => r.id === selectedRow);

      if (!foundRescuer) {
        return false;
      }

      selectedRescuers.push({
        id: foundRescuer.id,
        email_verified: foundRescuer.email_verified !== null,
        phone_verified: foundRescuer.phone_verified !== null,
      });
    });

    return (
      <Box pr={2}>
        <Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          {showSendEmailButton && (
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => onSendEmailButtonClick(selectedRescuers, this.state.selectedGroup)}>
                Send Email
              </Button>
            </Grid>
          )}

          {showSendSmsButton && (
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => onSendSMSButtonClick(selectedRescuers, this.state.selectedGroup)}>
                Send SMS
              </Button>
            </Grid>
          )}

          {showSendPushButton && (
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => onSendPushButtonClick(selectedRescuers, this.state.selectedGroup)}>
                Send Push
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  renderRolesTable = userRoles => {
    return (
      <Box m={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Assignment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userRoles.map((roleAssignment, index) => (
              <TableRow key={`sk_${roleAssignment.role_name}_${index}`}>
                <TableCell>{roleAssignment.role_name}</TableCell>
                <TableCell>{getRoleAssignment(roleAssignment)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  renderRolesFilter = (filterList, onChange, index, column) => {
    const { handleShowUsersWithAllSelectedRoles, previousTableState = {} } = this.props;
    const { showUsersWithAllSelectedRoles } = previousTableState;
    return (
      <>
        <FormControl variant="standard">
          <InputLabel htmlFor="select-role-selector">{column.label}</InputLabel>
          <Select
            variant="standard"
            id="select-role-selector"
            multiple
            value={filterList[index]}
            renderValue={selected => selected.join(', ')}
            onChange={event => {
              filterList[index] = event.target.value;
              onChange(filterList[index], index, column);
            } }
          >
            {formatUserRoles(Object.values(Roles)).map(role => (
              <MenuItem key={role} value={role}>
                <Checkbox color="primary" checked={filterList[index].indexOf(role) > -1} />
                <ListItemText primary={role} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Checkbox
              checked={!!showUsersWithAllSelectedRoles}
              color="secondary"
              onChange={() => handleShowUsersWithAllSelectedRoles(!showUsersWithAllSelectedRoles)}
            />
          }
          label={
            <Typography variant="subtitle1">
              Show users with <u>all</u> selected roles
            </Typography>
          }
        />
      </>
    );
  };

  renderRoles = (userRoles, allRoles) => (
    <HtmlTooltip withBorder title={this.renderRolesTable(allRoles)}>
      <Typography variant="body2">{userRoles}</Typography>
    </HtmlTooltip>
  );

  renderAffilationTable = userRoles => {
    const donorAndRaRoles = userRoles.filter((role) => role.donor_location_id || role.receiver_id);

    return (
      <Box m={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {donorAndRaRoles.map((roleAssignment, index) => (
              <TableRow key={`sk_${roleAssignment.role_name}_${index}`}>
                <TableCell>{getRoleAssignmentEntityType(roleAssignment)}</TableCell>
                <TableCell>{getRoleAssignment(roleAssignment)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  renderUserAffiliation = (userAffiliation, userRoles) => (
    <HtmlTooltip withBorder title={this.renderAffilationTable(userRoles)}>
      <Typography variant="body2">{truncate(userAffiliation, { length: 60 })}</Typography>
    </HtmlTooltip>
  );

  render() {
    const {
      isDeleting,
      selectedRows,
      selectedGroup,
      selectedAffiliations,
      selectedRowsDataIndexes,
      usersFromDifferentSite,
      usersWithoutPrimaryContact,
    } = this.state;

    const {
      tableId,
      title,
      users: usersProp,
      sendInvitation,
      showRolesColumn,
      showVerifiedColumn,
      showEditUserButton,
      showDeleteUserButton,
      showDeleteSiteCoordinatorButton,
      showReinstateUserButton = false,
      showImpersonateUserButton = false,
      showSentInvitationUserButton,
      showSelectableRows = false,
      showDifferentSiteAlert = false,
      onUserDelete,
      onSiteCoordinatorDelete,
      onUserReinstate,
      onImpersonateUserButtonClick,
      customToolbar = () => null,
      customRowActions = null,
      site,
      previousTableState = {},
      groupsList = [],
      locationState = {},
      affiliationsList = [],
      affiliationLoading = false,
    } = this.props;

    const { showUsersWithAllSelectedRoles } = previousTableState;

    const selectedUsers = locationState?.selectedUsers || [];
    const selectedUsersIds = new Set(selectedUsers.map((u) => u?.id));

    if (this._memoUsersInput !== usersProp || this._memoGroupInput !== selectedGroup) {
      this._memoUsersInput = usersProp;
      this._memoGroupInput = selectedGroup;
      const memberIds = selectedGroup ? new Set((selectedGroup.users || []).map(u => u.id)) : null;
      this._memoUsersOutput = memberIds ? (usersProp || []).filter(u => memberIds.has(u.id)) : usersProp;
    }

    // if users from lasso picker are selected sort them first
    const usersSorted = selectedUsersIds.size
      ? (this._memoUsersOutput || []).sort((a, b) => {
          const aSelected = selectedUsersIds.has(a?.id);
          const bSelected = selectedUsersIds.has(b?.id);
          if (aSelected !== bSelected) {
            return aSelected ? -1 : 1;
          }
          return 0;
        })
      : this._memoUsersOutput;

    // filter users if affiliations are selected
    const users =
      selectedAffiliations.length === 0
        ? usersSorted
        : (usersSorted || []).filter((user) =>
            (user?.role_assignments || []).some((role) =>
              selectedAffiliations.some((affiliation) => {
                if (affiliation.type === FOOD_DONOR_TYPE) {
                  return role.donor_location_id !== undefined && role.donor_location_id === affiliation.id;
                }

                if (affiliation.type === RECEIVER_TYPE) {
                  return role.receiver_id !== undefined && role.receiver_id === affiliation.id;
                }

                return false;
              })
            )
          );

    const fullNameCustomBodyRender = (value, tableMeta) => {
      const user = users[getMuiTableDataIndex(tableMeta)];

      if (!user) {
        return '';
      }

      return getUserFullName(user);
    };

    const emailVerifiedCustomBodyRender = (value) =>
      !isUndefinedOrNull(value) ? moment(value).format('MM/DD/YYYY') : 'Not Verified';

    const phoneVerifiedCustomBodyRender = (value) =>
      !isUndefinedOrNull(value) ? moment(value).format('MM/DD/YYYY') : 'Not Verified';

    const zipCodeCustomBodyRender = (value) => value || '-';

    const activeCustomBodyRender = (value) => (value ? 'Yes' : 'No');

    const roleAssignmentsCustomBodyRender = (value, csv = false) => {
      const userRoles = usersModel.getRolesFromRoleAssignments(
        authService.getUserRolesInCurrentlySelectedSite({ role_assignments: value })
      );

      if (!userRoles || !userRoles.length) {
        return !csv ? '-' : null;
      }

      const formattedUserRoles = formatUserRoles(userRoles).join(', ');

      if (csv) {
        return formattedUserRoles;
      }

      return this.renderRoles(formattedUserRoles, value);
    };

    const getUserAffiliationFormatted = (userRoles) => {
      const userAffiliation = (userRoles || [])
        .map((role) => {
          if (role.donor_location_name) {
            return role.donor_location_name;
          }

          if (role.receiver_name) {
            return role.receiver_name;
          }

          return null;
        })
        .filter((affiliation) => affiliation !== null);

      if (!userAffiliation.length) {
        return null;
      }

      return [...new Set(userAffiliation.map((a) => a.trim()))].join(', ');
    };

    const affiliationCustomBodyRender = (value, tableMeta, csv = false) => {
      const user = users[getMuiTableDataIndex(tableMeta)];
      const userRoles = user?.role_assignments || [];
      const userAffiliationFormatted = getUserAffiliationFormatted(userRoles);

      if (!userAffiliationFormatted) {
        return !csv ? '-' : null;
      }

      if (csv) {
        return userAffiliationFormatted;
      }

      return this.renderUserAffiliation(userAffiliationFormatted, userRoles);
    };

    return (
      <FiltersContext.Provider
        value={{
          groupsList,
          selectedGroup,
          onGroupChange: (group) => this.setState({ selectedGroup: group }),
          affiliationsList,
          affiliationLoading,
          handleAffiliationSelect: (affiliations) => this.setState({ selectedAffiliations: affiliations }),
        }}
      >
        <OverlayLoader isLoading={isDeleting}>
          {!!usersWithoutPrimaryContact.length && (
            <Alert severity="warning">
              <Typography variant="body1">
                Some of the selected locations are missing primary contact information:
              </Typography>
              <Typography variant="body2">
                {usersWithoutPrimaryContact.map((u) => u.title || u.name).join(', ')}
              </Typography>
            </Alert>
          )}
          {!!usersFromDifferentSite.length && showDifferentSiteAlert && (
            <Alert severity="info">
              <Typography variant="body1">
                Some of the selected users are from a different site. Only users from currently selected site will appear here.
              </Typography>
            </Alert>
          )}
          {selectedRows.length > 0 && (
            <TableToolbarSelect
              selectedRows={{
                data: selectedRows,
              }}
              options={{
                customToolbarSelect: this.renderCustomToolbarSelector,
                textLabels: {
                  selectedRows: {
                    text: 'user(s) selected',
                  },
                },
              }}
            />
          )}

          <BaseMUIDataTable
            tableId={tableId}
            title={title || 'Users'}
            data={users}
            columns={[
              {
                name: 'id',
                label: 'ID',
                options: {
                  filter: false,
                  sort: false,
                  display: false,
                },
              },
              {
                name: 'firstname',
                label: 'First Name',
                options: {
                  filter: false,
                  display: false,
                },
              },
              {
                name: 'lastname',
                label: 'Last Name',
                options: {
                  filter: false,
                  display: false,
                },
              },
              {
                name: 'fullname',
                label: 'Full Name',
                options: {
                  filter: false,
                  sortCompare: (order) =>
                    sortSelectedFirst(
                      (row1, row2) =>
                        sortAlphabetically(
                          order,
                          getUserFullName({
                            firstname: row1.rowData[1],
                            lastname: row1.rowData[2],
                          }),
                          getUserFullName({
                            firstname: row2.rowData[1],
                            lastname: row2.rowData[2],
                          })
                        ),
                      { getRowId: (item) => item.rowData[0], selectedIds: selectedUsersIds }
                    ),
                  customBodyRender: (value, tableMeta) => fullNameCustomBodyRender(value, tableMeta),
                  customBodyRenderCSV: (value, tableMeta) => fullNameCustomBodyRender(value, tableMeta),
                },
              },
              {
                name: 'email',
                label: 'E-mail',
                options: {
                  filter: false,
                },
              },
              {
                name: 'email_verified',
                label: 'Email Verified',
                options: {
                  filter: true,
                  display: false,
                  customFilterListOptions: {
                    render: (value) => `E-mail verified: ${value}`,
                  },
                  customBodyRender: (value) => emailVerifiedCustomBodyRender(value),
                  customBodyRenderCSV: (value) => emailVerifiedCustomBodyRender(value),
                },
              },
              {
                name: 'phone',
                label: 'Phone',
                options: {
                  filter: false,
                },
              },
              {
                name: 'phone_verified',
                label: 'Phone Verified',
                options: {
                  filter: true,
                  display: false,
                  ...userPhoneVerifiedFilterOptions,
                  customBodyRender: (value) => phoneVerifiedCustomBodyRender(value),
                  customBodyRenderCSV: (value) => phoneVerifiedCustomBodyRender(value),
                },
              },
              {
                name: 'zip_code',
                label: 'Zip Code',
                options: {
                  filter: true,
                  filterType: 'multiselect',
                  customFilterListOptions: {
                    render: (value) => `Zip Code: ${value}`,
                  },
                  customBodyRender: (value) => zipCodeCustomBodyRender(value),
                  customBodyRenderCSV: (value) => zipCodeCustomBodyRender(value),
                },
              },
              {
                name: 'group_filter',
                label: 'Group',
                options: {
                  filter: false,
                  display: false,
                  sort: false,
                  download: false,
                  customFilterListOptions: {
                    render: value => `Group: ${value}`,
                  },
                  filterOptions: {
                    logic: () => false,
                  },
                },
              },
              {
                name: 'active',
                label: 'Active',
                options: {
                  customFilterListOptions: {
                    render: (value) => `Is active: ${value}`,
                  },
                  customBodyRender: (value) => activeCustomBodyRender(value),
                  customBodyRenderCSV: (value) => activeCustomBodyRender(value),
                },
              },
              {
                name: 'role_assignments',
                label: 'Roles',
                options: {
                  display: showRolesColumn,
                  filterType: 'custom',
                  sortCompare: (order) =>
                    sortSelectedFirst(
                      ({ data: roles1 }, { data: roles2 }) => {
                        const userRoles1 = usersModel.getRolesFromRoleAssignments(
                          authService.getUserRolesInCurrentlySelectedSite({ role_assignments: formatUserRoles(roles1) })
                        );
                        const userRoles2 = usersModel.getRolesFromRoleAssignments(
                          authService.getUserRolesInCurrentlySelectedSite({ role_assignments: formatUserRoles(roles2) })
                        );

                        if (order === 'asc') {
                          return rolesWeights[userRoles2[0]] - rolesWeights[userRoles1[0]];
                        }

                        return rolesWeights[userRoles1[0]] - rolesWeights[userRoles2[0]];
                      },
                      { getRowId: (item) => item.rowData[0], selectedIds: selectedUsersIds }
                    ),
                  customFilterListOptions: {
                    render: value => `Role: ${value.join(', ')}`,
                  },
                  filterOptions: {
                    logic: (roleAssignments, filters) => {
                      if (!filters || filters.length === 0) {
                        return false;
                      }

                      if (showUsersWithAllSelectedRoles) {
                        return !filters.every((filter) =>
                          Object.values(roleAssignments).find((role) => formatUserRole(role.role_name) === filter)
                        );
                      }

                      return !Object.values(roleAssignments).some(role => filters.includes(formatUserRole(role.role_name)));
                    },
                    display: this.renderRolesFilter,
                  },
                  customBodyRender: value => roleAssignmentsCustomBodyRender(value),
                  customBodyRenderCSV: value => roleAssignmentsCustomBodyRender(value, true),
                },
              },
              {
                name: 'affiliation',
                label: 'Affiliation',
                options: {
                  display: showRolesColumn,
                  filter: false,
                  sort: false,
                  customBodyRender: (value, tableMeta) => affiliationCustomBodyRender(value, tableMeta),
                  customBodyRenderCSV: (value, tableMeta) => affiliationCustomBodyRender(value, tableMeta, true),
                },
              },
              {
                name: 'completed_registration',
                label: 'Registration',
                options: {
                  display: showVerifiedColumn,
                  customFilterListOptions: {
                    render: (value) => `Completed registration: ${formatDate(value)}`,
                  },
                  ...userRegistrationFilterOptions,
                  customBodyRenderLite: dataIndex => {
                    const user = users[dataIndex];

                    if (user.completed_registration !== null) {
                      return formatDate(user.completed_registration);
                    }

                    if (showSentInvitationUserButton && user) {
                      return (
                        <>
                          {!user.invitation_sent && (
                            <Button
                              startIcon={<MailIcon fontSize="small" />}
                              type="button"
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => sendInvitation(user)}
                              data-testid="add-new-user-button"
                            >
                              {!user.invitation_sent && 'No, invite'}
                            </Button>
                          )}
                          {user.invitation_sent && (
                            <CopyInviteLinkSplitButton
                              type="button"
                              variant="contained"
                              color="primary"
                              size="small"
                              onInviteClick={() => sendInvitation(user)}
                              data-testid="add-new-user-button"
                              label={<>No, invite again ({moment(user.invitation_sent).format('MM/DD/YYYY h:mm A')})</>}
                              userId={user.id}
                            />
                          )}
                        </>
                      );
                    }
                  },
                  customBodyRenderCSV: (value, tableMeta) => {
                    const user = users[getMuiTableDataIndex(tableMeta)];

                    if (!user) {
                      return null;
                    }

                    return user.completed_registration !== null ? formatDate(user.completed_registration) : 'No';
                  },
                },
              },
              {
                name: 'actions',
                label: 'Actions',
                options: {
                  download: false,
                  filter: false,
                  sort: false,
                  display:
                    showEditUserButton ||
                    showImpersonateUserButton ||
                    showSentInvitationUserButton ||
                    showDeleteUserButton ||
                    showDeleteSiteCoordinatorButton ||
                    showReinstateUserButton ||
                    !!customRowActions,
                  customBodyRender: (value, tableMeta) => {
                    const user = users[getMuiTableDataIndex(tableMeta)];

                    // a user who left Food Rescue US has no account to edit or impersonate, they can only be reinstated
                    if (user?.left_at) {
                      return (
                        <TableActionsButtonsWrapper>
                          {showReinstateUserButton && (
                            <TableActionButton
                              title="Reinstate"
                              icon={RestoreIcon}
                              onClick={() => {
                                confirmAlert({
                                  title: `Reinstate ${getUserFullName(user)}?`,
                                  message: 'This will restore their account and site memberships and send them an email'
                                    + ' to set a new password.',
                                  buttons: [
                                    {
                                      label: 'Cancel',
                                      color: 'primary',
                                    },
                                    {
                                      label: 'Reinstate',
                                      color: 'primary',
                                      onClick: () => {
                                        this.setState({ isDeleting: true }, () => {
                                          onUserReinstate(user).finally(() => {
                                            this.setState({ isDeleting: false });
                                          });
                                        });
                                      },
                                    },
                                  ],
                                  customUI: ({ title, message, onClose, buttons }) => (
                                    <ConfirmationDialog
                                      buttons={buttons}
                                      closeDialog={onClose}
                                      title={title}
                                      message={message}
                                    />
                                  ),
                                });
                              }}
                            />
                          )}
                        </TableActionsButtonsWrapper>
                      );
                    }

                    return (
                      <TableActionsButtonsWrapper>
                        {showEditUserButton && (
                          <TableActionButton
                            title="Edit User"
                            buttonProps={{
                              component: Link,
                              to: generatePath(routes.userEdit, { id: tableMeta.rowData[0] }),
                            }}
                          />
                        )}

                        {showDeleteUserButton && (
                          <TableActionButton
                            title="Delete User"
                            icon={DeleteIcon}
                            onClick={() => {
                              confirmAlert({
                                title: 'Delete this user?',
                                message: 'Are you sure you want to delete this user?',
                                buttons: [
                                  {
                                    label: 'No',
                                    color: 'primary',
                                  },
                                  {
                                    label: 'Yes',
                                    color: 'primary',
                                    onClick: () => {
                                      this.setState({ isDeleting: true }, () => {
                                        onUserDelete(user).then(() => {
                                          this.setState({ isDeleting: false });
                                        });
                                      });
                                    },
                                  },
                                ],
                                customUI: ({ title, message, onClose, buttons }) => (
                                  <ConfirmationDialog
                                    buttons={buttons}
                                    closeDialog={onClose}
                                    title={title}
                                    message={message}
                                  />
                                ),
                              });
                            }}
                          />
                        )}

                        {(
                          // show delete (detach) site director button for
                          // users with SC role but without SD role attached
                          showDeleteSiteCoordinatorButton
                          && (user?.role_assignments || []).some(ra => ra.role_name === Roles.SiteCoordinator)
                          && (user?.role_assignments || []).every(ra => ra.role_name !== Roles.SiteDirector)
                        ) && (
                          <TableActionButton
                            title="Delete Site Coordinator"
                            icon={DeleteIcon}
                            onClick={() => {
                              confirmAlert({
                                title: 'Delete this site coordinator?',
                                message: 'Are you sure you want to delete this site coordinator?',
                                buttons: [
                                  {
                                    label: 'No',
                                    color: 'primary',
                                  },
                                  {
                                    label: 'Yes',
                                    color: 'primary',
                                    onClick: () => {
                                      this.setState({ isDeleting: true }, () => {
                                        onSiteCoordinatorDelete(user).then(() => {
                                          this.setState({ isDeleting: false });
                                        });
                                      });
                                    },
                                  },
                                ],
                                customUI: ({ title, message, onClose, buttons }) => (
                                  <ConfirmationDialog
                                    buttons={buttons}
                                    closeDialog={onClose}
                                    title={title}
                                    message={message}
                                  />
                                ),
                              });
                            }}
                          />
                        )}

                        {showImpersonateUserButton && (
                          <TableActionButton
                            title="Impersonate User"
                            icon={SyncAlt}
                            onClick={() => onImpersonateUserButtonClick(tableMeta.rowData[0])}
                          />
                        )}

                        {customRowActions && customRowActions(user)}
                      </TableActionsButtonsWrapper>
                    );
                  },
                },
              },
            ]}
            options={{
              customSearchRender: debounceSearchRender(500),
              customSearch: (searchQuery, currentRow, columns) => {
                const firstNameIndex = columns.findIndex(({ name }) => name === 'firstname');
                const lastNameIndex = columns.findIndex(({ name }) => name === 'lastname');
                const rolesIndex = columns.findIndex(({ name }) => name === 'role_assignments');
                const affiliationIndex = columns.findIndex(({ name }) => name === 'affiliation');
                const roleAssignments = currentRow[rolesIndex];

                const searchableRow = currentRow.map((cell, index) => {
                  if (index === rolesIndex) {
                    return roleAssignmentsCustomBodyRender(roleAssignments, true);
                  }

                  if (index === affiliationIndex) {
                    return getUserAffiliationFormatted(roleAssignments);
                  }

                  return cell;
                });

                const { numberOfMatches, fullMatch } = superSearch(searchQuery.toLowerCase(), {
                  ...[`${currentRow[firstNameIndex]} ${currentRow[lastNameIndex]}`, ...searchableRow],
                });

                return fullMatch && numberOfMatches > 0;
              },
              responsive: 'simple',
              customToolbar: customToolbar,
              selectableRows: showSelectableRows ? 'multiple' : 'none',
              // users who left Food Rescue US can't be emailed or texted until they are reinstated
              isRowSelectable: (dataIndex) => !users[dataIndex]?.left_at,
              selectToolbarPlacement: 'none', // TODO: now supports 'above' - update component to use it, instead of custom function
              rowsSelected: selectedRowsDataIndexes,
              onRowSelectionChange: (currentRowsSelected, allRowsSelected) => {
                const newSelectedRowsDataIndexes = allRowsSelected.map((row) => row.dataIndex);
                const selectedIds = allRowsSelected.map((row) => users[row.dataIndex]?.id);

                this.setState({ selectedRows: selectedIds, selectedRowsDataIndexes: newSelectedRowsDataIndexes });
                if (this.props.onRowSelectionChange) {
                  this.props.onRowSelectionChange(selectedIds);
                }
              },
              customSort: (data, colIndex, order) =>
                [...data].sort(
                  sortSelectedFirst(
                    (item1, item2) => {
                      const val1 = item1.data[colIndex];
                      const val2 = item2.data[colIndex];
                      if (val1 < val2) return order === 'asc' ? -1 : 1;
                      if (val1 > val2) return order === 'asc' ? 1 : -1;
                      return 0;
                    },
                    { getRowId: (item) => item?.data?.[0], selectedIds: selectedUsersIds }
                  )
                ),
              downloadOptions: {
                filename: generateCustomCsvFileName({
                  baseFileName: site?.name || 'All Users',
                  customSuffix: site?.name ? 'All Users' : undefined
                }),
              },
            }}
            components={{
              TableFilterList: CustomFilterList,
            }}
          />
        </OverlayLoader>
      </FiltersContext.Provider>
    );
  }
}

export default UsersTable;
